import { Typography } from '@mui/material';
import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Tablet } from '@/features/upload';
import { Control, FieldErrors } from 'react-hook-form';
import { CreateAdFormData } from '@/pages/ad-pages/CreateAd';
import { QrCodeForm } from '@/pages/ad-pages/components/QrCodeForm';
import { Card } from '@/features/card';
import { ProductType } from '@/models/ad';
import { Button } from '@/components/Button';

type Props = {
  productType: ProductType;
  qrUrl?: string;
  qrText?: string;
  control: Control<CreateAdFormData>;
  errors: FieldErrors<CreateAdFormData>;
  onTypeSelect: (type: ProductType) => void;
  onUpload: (file: File) => void;
};

type AdParametersType = {
  label: string;
  productType: ProductType;
  display_time: string;
  formats: string;
  size: string;
};

const config: Record<ProductType, AdParametersType> = {
  [ProductType.Image]: {
    label: 'media_section.media_type.image.title',
    productType: ProductType.Image,
    display_time: 'media_section.media_type.image.display_time',
    formats: 'media_section.message.supported_images_formats',
    size: 'media_section.max_image_size_limitation',
  },
  [ProductType.Video15s]: {
    label: 'media_section.media_type.video_15s.title',
    productType: ProductType.Video15s,
    display_time: 'media_section.media_type.video_15s.display_time',
    formats: 'media_section.message.supported_video_formats',
    size: 'media_section.max_video_size_limitation',
  },
  [ProductType.Video30s]: {
    label: 'media_section.media_type.video_30s.title',
    productType: ProductType.Video30s,
    display_time: 'media_section.media_type.video_30s.display_time',
    formats: 'media_section.message.supported_video_formats',
    size: 'media_section.max_video_size_limitation',
  },
  [ProductType.CustomVideo]: {
    label: 'media_section.media_type.custom_image.title',
    productType: ProductType.CustomVideo,
    display_time: 'media_section.media_type.custom_image.display_time',
    formats: 'media_section.message.supported_video_formats',
    size: 'media_section.media_type.custom_image.size',
  },
  [ProductType.CustomImage]: {
    label: 'media_section.media_type.custom_video.title',
    productType: ProductType.CustomImage,
    display_time: 'media_section.media_type.custom_video.display_time',
    formats: 'media_section.message.supported_video_formats',
    size: 'media_section.media_type.custom_video.size',
  },
};

export const MediaSection: FC<Props> = ({
  qrUrl,
  qrText,
  control,
  errors,
  onTypeSelect,
  onUpload,
}) => {
  const [showQrForm, setShowQrForm] = useState(false);
  const [selectedType, setSelectedType] = useState<ProductType>(
    ProductType.Image
  );
  const { t } = useTranslation();

  const handleUpload = (file: File) => {
    setShowQrForm(true);
    onUpload(file);
  };

  const handleSelect = (type: ProductType) => {
    setSelectedType(type);
    onTypeSelect(type);
  };

  const isCustomProduct =
    selectedType === ProductType.CustomVideo ||
    selectedType === ProductType.CustomImage;

  return (
    <div className="w-full">
      <Typography variant="h6" sx={{ mb: '14px' }}>
        <Trans i18nKey="media_section.title" />
      </Typography>
      <div className="w-full">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-4">
          {Object.keys(config).map((key) => (
            <Button
              key={key}
              variant="contained"
              color={
                config[selectedType].productType === key
                  ? 'secondary'
                  : 'primary'
              }
              onClick={() =>
                handleSelect(config[key as ProductType].productType)
              }
            >
              <Trans i18nKey={config[key as ProductType].label} />
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <Card>
            <Typography variant="body1">
              <Trans i18nKey="media_section.label.formats" />:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              <Trans i18nKey={config[selectedType].formats} />
            </Typography>
          </Card>
          <Card>
            <Typography variant="body1">
              <Trans i18nKey="media_section.label.display_time" />:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              <Trans i18nKey={config[selectedType].display_time} />
            </Typography>
          </Card>
          <Card>
            <Typography variant="body1">
              <Trans i18nKey="media_section.label.size" />:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              <Trans i18nKey={config[selectedType].size} />
            </Typography>
          </Card>
        </div>

        <div className="flex justify-center w-full mb-5">
          <Tablet
            onUpload={!isCustomProduct ? handleUpload : undefined}
            accept={config[selectedType].productType}
            productType={config[selectedType].productType}
            qrUrl={qrUrl}
            qrText={qrText}
            disabled={isCustomProduct}
            disabledMessage={
              isCustomProduct ? t('media_section.message.custom') : undefined
            }
          />
        </div>
      </div>
      {showQrForm && <QrCodeForm control={control} errors={errors} />}
    </div>
  );
};
